/* @noflow FIXME */
import { get, isNumber } from 'lodash';
import pluralize from 'pluralize';
import * as React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import AdminStatisticsChartTooltip from 'admin/components/AdminStatisticsChartTooltip';
import AdminStatisticsChartTooltipValue from 'admin/components/AdminStatisticsChartTooltipValue';
import AdminStatisticsChartXAxisTick from 'admin/components/AdminStatisticsChartXAxisTick';
import {
  POOL,
  CHART_AXIS_FILL,
  CHART_BAR_SIZE,
  CHART_GRID_COLOR,
  CHART_HEIGHT,
  CHART_HEIGHT_SIMPLE,
  CHART_LEGEND_ALIGN,
  CHART_LEGEND_ICON_SIZE,
  CHART_MARGIN,
  CHART_MARKER_LEGEND_TYPE,
  GLOBE,
  PENNANT,
} from 'admin/constants/Charts';
import Row from 'components/Row';
import Text from 'components/Text';

type Props = {
  data: Array<Object>,
  height?: number,
  legend?: boolean,
  parents?: boolean,
  parentsColor?: string,
  simple?: boolean,
  students?: boolean,
  studentsColor?: string,
  teachers?: boolean,
  teachersColor?: string,
  tooltip?: boolean,
};

function StatisticsTeachersReachChartTooltip(
  // $FlowIssue
  props: { active: boolean, label: string, payload: Array }
) {
  const { active } = props;

  if (!active) {
    return null;
  }

  const { label, payload } = props;

  // when not showing axis, the `title` is just an index & not the date
  const title = isNumber(label) ? get(payload, '[0].payload.Date') : label;

  return (
    <AdminStatisticsChartTooltip title={title || label}>
      <StatisticsStudentsParentsReachChartTooltipValue
        action="sent"
        label={payload[0].name}
        value={payload[0].value}
      />
    </AdminStatisticsChartTooltip>
  );
}

function StatisticsStudentsParentsReachChartTooltip(
  // $FlowIssue
  props: { active: boolean, label: string, payload: Array }
) {
  const { active } = props;

  if (!active) {
    return null;
  }

  const { label, payload } = props;

  // when not showing axis, the `title` is just an index & not the date
  const title = isNumber(label) ? get(payload, '[0].payload.Date') : label;

  return (
    <AdminStatisticsChartTooltip title={title || label}>
      <Row size="small">
        <StatisticsStudentsParentsReachChartTooltipValue
          action="received"
          label={payload[0].name}
          value={payload[0].value}
        />
      </Row>
      <StatisticsStudentsParentsReachChartTooltipValue
        action="received"
        label={payload[1].name}
        value={payload[1].value}
      />
    </AdminStatisticsChartTooltip>
  );
}

function StatisticsStudentsParentsReachChartTooltipValue(props: {
  action: string,
  label: string,
  value: number,
}) {
  const { action, label, value } = props;

  return (
    <div>
      <AdminStatisticsChartTooltipValue element="div">
        {value.toLocaleString()}
      </AdminStatisticsChartTooltipValue>
      {pluralize(label, value)} <Text weight="semibold">{action}</Text> messages
    </div>
  );
}

export default function AdminStatisticsReachChart({
  data,
  height,
  legend,
  parents,
  parentsColor,
  simple,
  students,
  studentsColor,
  teachers,
  teachersColor,
  tooltip,
}: Props) {
  const chartHeight = simple ? CHART_HEIGHT_SIMPLE : CHART_HEIGHT;

  return (
    <div className="AdminStatisticsReachChart">
      <ResponsiveContainer width="100%" height={height || chartHeight}>
        <BarChart barGap={0} data={data} margin={CHART_MARGIN}>
          {!simple && (
            <XAxis
              axisLine={false}
              dataKey="Date"
              // $FlowIssue
              tick={<AdminStatisticsChartXAxisTick />}
              tickLine={false}
            />
          )}
          {!simple && (
            <YAxis
              allowDecimals={false}
              axisLine={false}
              tick={{ fill: CHART_AXIS_FILL }}
              tickLine={false}
            />
          )}
          {!simple && <CartesianGrid stroke={CHART_GRID_COLOR} vertical={false} />}
          {(tooltip || !simple) && (
            <Tooltip
              content={
                teachers || !students ? (
                  // $FlowIssue
                  <StatisticsTeachersReachChartTooltip />
                ) : (
                  // $FlowIssue
                  <StatisticsStudentsParentsReachChartTooltip />
                )
              }
              isAnimationActive={false}
            />
          )}
          {(legend || !simple) && (
            <Legend align={CHART_LEGEND_ALIGN} iconSize={CHART_LEGEND_ICON_SIZE} />
          )}
          {teachers && (
            <Bar
              barSize={CHART_BAR_SIZE}
              dataKey="Teachers"
              fill={teachersColor}
              legendType={CHART_MARKER_LEGEND_TYPE}
            />
          )}
          {students && (
            <Bar
              barSize={CHART_BAR_SIZE}
              dataKey="Students"
              fill={studentsColor}
              legendType={CHART_MARKER_LEGEND_TYPE}
            />
          )}
          {parents && (
            <Bar
              barSize={CHART_BAR_SIZE}
              dataKey="Parents"
              fill={parentsColor}
              legendType={CHART_MARKER_LEGEND_TYPE}
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

AdminStatisticsReachChart.defaultProps = {
  legend: true,
  parents: true,
  parentsColor: POOL,
  students: true,
  studentsColor: PENNANT,
  teachers: true,
  teachersColor: GLOBE,
  tooltip: true,
};
